import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import SectionHeader from '../components/SectionHeader';
import Pill from '../components/Pill';

const ROW_LIMIT = 200;

// Writeback issues = Cin7 never told about the dispatch (Starshipit only;
// 'Not required' is normal for orders that didn't come from Cin7).
const FILTERS = [
  { key: 'all',         label: 'All' },
  { key: 'undelivered', label: 'Not Delivered' },
  { key: 'writeback',   label: 'Cin7 Writeback Issues' },
];

const SOURCES = [
  { key: 'all',        label: 'Both Systems' },
  { key: 'starshipit', label: 'Starshipit' },
  { key: 'zappy',      label: 'Zappy' },
];

const VIEWS = [
  { key: 'shipments', label: 'Shipments' },
  { key: 'address',   label: 'Address Check' },
];

const DAYS = [
  { key: '30', label: '30 Days' },
  { key: '90', label: '90 Days' },
];

const issueColor = (issue) => {
  if (issue.startsWith('Postcode')) return 'red';
  if (issue === 'Street number')    return 'red';
  if (issue === 'Town misspelt')    return 'gold';
  return 'muted';
};

const joinAddr = (...parts) => parts.map(p => (p || '').trim()).filter(Boolean).join(', ') || '—';

const trunc = (str, len) => {
  if (!str) return '—';
  return str.length > len ? str.slice(0, len) + '…' : str;
};

const statusColor = (r) => {
  if (!r.status)  return 'muted';
  if (r.delivered) return 'green';
  const v = r.status.toLowerCase();
  if (v.includes('cancel'))                              return 'muted';
  if (v.includes('exception') || v.includes('attempt')) return 'red';
  if (v.includes('transit') || v.includes('dispatch') || v.includes('board')
      || v.includes('picked') || v.includes('depot'))    return 'blue';
  return 'gold';
};

const writebackColor = (s) => {
  if (s === 'Up to date')   return 'green';
  if (s === 'Not required') return 'muted';
  return 'red';
};

const nzDateTime = (ts) => ts
  ? new Date(ts).toLocaleString('en-NZ', {
      timeZone: 'Pacific/Auckland', day: '2-digit', month: '2-digit',
      year: '2-digit', hour: '2-digit', minute: '2-digit',
    })
  : '—';

const lastSync = async (table) => {
  const { data } = await supabase.from(table)
    .select('last_synced_at')
    .order('last_synced_at', { ascending: false })
    .limit(1);
  return data?.[0]?.last_synced_at || null;
};

// ── AddressCheck ──────────────────────────────────────────────────────────────
// What the customer entered on the order vs the address the courier label was
// printed with (Starshipit destination fetched by [52], Zappy scraped by [53]),
// via get_address_mismatches — only orders that differ. The issue chips count
// each kind of difference, so the common entry errors stand out; click one to
// list just those orders.
const AddressCheck = ({ source, tabs, setParam, params }) => {
  const days                  = params.get('d') || '30';
  const issue                 = params.get('i') || '';
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const { data, error: err } = await supabase.rpc('get_address_mismatches', {
      p_days: Number(days), p_source: source,
    });
    if (err) setError(err.message);
    setRows(data || []);
    setLoading(false);
  }, [days, source]);

  useEffect(() => { load(); }, [load]);

  const counts = {};
  rows.forEach(r => (r.issues || []).forEach(i => { counts[i] = (counts[i] || 0) + 1; }));
  const chips = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  const shown = issue ? rows.filter(r => (r.issues || []).includes(issue)) : rows;

  return (
    <section className="section">
      <SectionHeader
        title={issue ? `Address Differences: ${issue}` : 'Address Differences'}
        count={shown.length}
        onRefresh={load}
        loading={loading}
      />
      <p className="section__desc">
        Orders where the address the customer entered differs from the address on the courier
        label — so it was corrected before dispatch, or went out wrong. Starshipit addresses
        are fetched hourly as orders ship; Zappy's come with each consignment. Town differences
        are only flagged when neither side's town appears anywhere in the other address
        (customers often put their suburb in the city field).
      </p>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
        {tabs(DAYS, days, 'd')}
        {tabs(SOURCES, source, 's')}
      </div>

      {chips.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          <button
            className={`action-btn ${!issue ? 'action-btn--reset' : 'action-btn--ghost'}`}
            onClick={() => setParam('i', '')}
          >
            All Issues ({rows.length})
          </button>
          {chips.map(([label, n]) => (
            <button
              key={label}
              className={`action-btn ${issue === label ? 'action-btn--reset' : 'action-btn--ghost'}`}
              onClick={() => setParam('i', label)}
            >
              {label} ({n})
            </button>
          ))}
        </div>
      )}

      {error && <div className="empty-row" style={{ color: 'var(--red)' }}>{error}</div>}
      {loading ? (
        <div className="loading-row">Loading...</div>
      ) : !shown.length ? (
        <div className="empty-row">No address differences found</div>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Order</th>
                <th>System</th>
                <th>Shipped (NZ)</th>
                <th>Customer</th>
                <th>Entered by Customer</th>
                <th>Postcode</th>
                <th>On Courier Label</th>
                <th>Postcode</th>
                <th>Issues</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r, i) => (
                <tr key={`${r.source}-${r.order_ref}-${i}`} className="data-row">
                  <td><span className="code-badge">{r.order_ref}</span></td>
                  <td className="muted">{r.source}</td>
                  <td className="muted">{nzDateTime(r.shipped_at)}</td>
                  <td>{trunc(r.customer_name, 22)}</td>
                  <td>{joinAddr(r.entered_address1, r.entered_address2, r.entered_city)}</td>
                  <td>{r.entered_post_code || '—'}</td>
                  <td title={r.address_validation ? `Starshipit address check: ${r.address_validation}` : ''}>
                    {joinAddr(r.label_street, r.label_suburb, r.label_city)}
                  </td>
                  <td>{r.label_post_code || '—'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {(r.issues || []).map(x => <Pill key={x} label={x} color={issueColor(x)} />)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

// ── DispatchTracking Page ─────────────────────────────────────────────────────
// Shipments from both despatch systems in one list, via get_tracking_lookup:
// starshipit_tracking (52, hourly) and zappy_consignments (53, hourly).
// Newest ROW_LIMIT first; the search runs server-side over everything stored.
const DispatchTracking = () => {
  const navigate = useNavigate();
  const [params, setParams]   = useSearchParams();
  const q                     = params.get('q') || '';
  const filter                = params.get('f') || 'all';
  const source                = params.get('s') || 'all';
  const view                  = params.get('v') || 'shipments';
  const [input, setInput]     = useState(q);
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [syncs, setSyncs]     = useState({});

  const setParam = (key, value) => {
    const next = Object.fromEntries(params.entries());
    if (value && value !== 'all') next[key] = value; else delete next[key];
    setParams(next);
  };

  const load = useCallback(async () => {
    if (view !== 'shipments') return;
    setLoading(true);
    setError('');
    const [{ data, error: err }, starshipit, zappy] = await Promise.all([
      supabase.rpc('get_tracking_lookup', {
        p_search: q || null, p_filter: filter, p_source: source, p_limit: ROW_LIMIT,
      }),
      lastSync('starshipit_tracking'),
      lastSync('zappy_consignments'),
    ]);
    if (err) setError(err.message);
    setRows(data || []);
    setSyncs({ starshipit, zappy });
    setLoading(false);
  }, [q, filter, source, view]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (e) => {
    e.preventDefault();
    const v = input.trim();
    setInput(v);
    setParam('q', v);
  };

  const handleClear = () => {
    setInput('');
    setParam('q', '');
  };

  const title = q ? `Shipments matching ${q}` : `Latest ${ROW_LIMIT} Shipments`;

  const tabs = (items, current, key) => (
    <div style={{ display: 'flex', gap: 6 }}>
      {items.map(t => (
        <button
          key={t.key}
          className={`action-btn ${current === t.key ? 'action-btn--reset' : 'action-btn--ghost'}`}
          onClick={() => setParam(key, t.key)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );

  return (
    <main className="app-main">
      <div className="page-nav">
        <button className="back-btn" onClick={() => navigate('/dispatch-tools')}>← Dispatch Tools</button>
        <span className="page-nav__title">Dispatch Tracking</span>
      </div>

      <div style={{ marginBottom: 12 }}>{tabs(VIEWS, view, 'v')}</div>

      {view === 'address' ? (
        <AddressCheck source={source} tabs={tabs} setParam={setParam} params={params} />
      ) : (
      <section className="section">
        <SectionHeader
          title={title}
          count={rows.length}
          onRefresh={load}
          loading={loading}
          actions={
            <form onSubmit={handleSearch} style={{ display: 'inline-flex', gap: 6, marginRight: 8 }}>
              <input
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder="Order, customer, tracking or consignment no"
                style={{
                  background: 'var(--bg3)', color: 'var(--white)',
                  border: '1px solid var(--border2)', borderRadius: 4,
                  padding: '4px 10px', fontFamily: 'var(--font-body)', fontSize: 12, width: 260,
                }}
              />
              <button type="submit" className="action-btn action-btn--reset">Search</button>
              {q && <button type="button" className="action-btn action-btn--ghost" onClick={handleClear}>Clear</button>}
            </form>
          }
        />
        <p className="section__desc">
          Shipments from Starshipit and Zappy (last 90 days), both synced hourly — newest first.
          Search covers everything stored, not just these. Click a tracking number for the
          carrier's tracking page. Zappy statuses stop updating 14 days after the consignment
          was created; Cin7 writeback status is Starshipit only.
          <span className="muted" style={{ marginLeft: 8 }}>
            Last sync: Starshipit {nzDateTime(syncs.starshipit)} · Zappy {nzDateTime(syncs.zappy)}
          </span>
        </p>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
          {tabs(FILTERS, filter, 'f')}
          {tabs(SOURCES, source, 's')}
        </div>

        {error && <div className="empty-row" style={{ color: 'var(--red)' }}>{error}</div>}
        {loading ? (
          <div className="loading-row">Loading...</div>
        ) : !rows.length ? (
          <div className="empty-row">No shipments found</div>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>System</th>
                  <th>Customer</th>
                  <th>Country</th>
                  <th title="Starshipit: shipped. Zappy: consignment created.">Shipped (NZ)</th>
                  <th>Carrier</th>
                  <th>Service / Consignment</th>
                  <th>Tracking No</th>
                  <th>Status</th>
                  <th>Cin7 Writeback</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.source}-${r.consignment_ref || r.order_ref}-${i}`} className="data-row">
                    <td>{r.order_ref ? <span className="code-badge">{r.order_ref}</span> : '—'}</td>
                    <td className="muted">{r.source}</td>
                    <td>{trunc(r.customer_name, 24)}</td>
                    <td className="muted">{r.country || '—'}</td>
                    <td className="muted">{nzDateTime(r.event_at)}</td>
                    <td>{r.carrier || '—'}</td>
                    <td className="muted">{trunc(r.service || r.consignment_ref, 28)}</td>
                    <td>
                      {r.tracking_url ? (
                        <a href={r.tracking_url} target="_blank" rel="noopener noreferrer">
                          {r.tracking_numbers || 'Track'}
                        </a>
                      ) : (r.tracking_numbers || '—')}
                    </td>
                    <td><Pill label={r.status || '—'} color={statusColor(r)} /></td>
                    <td title={r.writeback_details || ''}>
                      {r.writeback_status
                        ? <Pill label={r.writeback_status} color={writebackColor(r.writeback_status)} />
                        : <span className="muted">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      )}
    </main>
  );
};

export default DispatchTracking;
